import * as React from "react";
import {
  Popover,
  Stack,
  TextField,
  MenuItem,
  Typography,
  Button,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import dayjs from "dayjs";
import { useQueryClient } from "react-query";
import { API_BASE_URL } from "../../config";
import { useAlert } from "../../contexts/AlertProvider";
import { useUser } from "../../contexts/UserProvider";

const CONTACT_TYPES = ["Phone", "Email", "Face to Face", "Other"];

// One-step "log a contact" form for a client row. Posts to the same endpoint
// as the full contact-history dialog, with today's date prefilled.
export default function QuickLogPopover({ anchorEl, onClose, row }) {
  const { alert } = useAlert();
  const queryClient = useQueryClient();
  const { firstName, lastName } = useUser();
  const [contactType, setContactType] = React.useState("Phone");
  const [notes, setNotes] = React.useState("");
  const [saving, setSaving] = React.useState(false);

  const handleClose = () => {
    setNotes("");
    setContactType("Phone");
    onClose();
  };

  const save = async () => {
    setSaving(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/policyholders/contact-history/${row.policyId}/insert`,
        {
          method: "POST",
          mode: "cors",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            contactedAt: dayjs().toISOString(),
            contactType,
            notes: notes.trim(),
            createdBy: `${firstName} ${lastName}`,
          }),
        }
      );
      const result = await response.json();

      if (!response.ok || result.status === "WARNING" || result.status === "FAILURE") {
        alert({
          title: "Contact Not Logged",
          detail: result.failureReason || "Unable to log contact at the moment",
          severity: "warning",
          duration: 8000,
        });
        return;
      }

      alert({
        title: "Contact Logged",
        detail: `${contactType} contact added for ${row.firstName} ${row.lastName}`,
        severity: "success",
        duration: 5000,
      });
      queryClient.invalidateQueries(["lastContact", row.policyId]);
      handleClose();
    } catch {
      alert({
        title: "Contact History Error",
        detail: "Unable to log contact at the moment",
        severity: "error",
        duration: 5000,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Popover
      open={Boolean(anchorEl) && Boolean(row)}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
    >
      <Stack
        component="form"
        spacing={1.5}
        sx={{ width: 300, p: 2 }}
        onSubmit={(event) => {
          event.preventDefault();
          if (notes.trim()) save();
        }}
      >
        <Typography sx={{ fontWeight: 600, fontSize: "14px" }}>
          Log contact{row ? ` for ${row.firstName} ${row.lastName}` : ""}
        </Typography>
        <TextField
          select
          size="small"
          label="Contact type"
          value={contactType}
          onChange={(event) => setContactType(event.target.value)}
        >
          {CONTACT_TYPES.map((type) => (
            <MenuItem key={type} value={type}>
              {type}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          label="Notes"
          required
          multiline
          minRows={3}
          autoFocus
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          onKeyDown={(event) => {
            if ((event.metaKey || event.ctrlKey) && event.key === "Enter" && notes.trim() && !saving) {
              event.preventDefault();
              save();
            }
          }}
          helperText={`Dated today, ${dayjs().format("MM/DD/YYYY")}`}
        />
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button variant="outlined" size="small" onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            size="small"
            type="submit"
            loading={saving}
            disabled={!notes.trim()}
          >
            Log contact
          </LoadingButton>
        </Stack>
      </Stack>
    </Popover>
  );
}
